import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { FooterHTAG } from '../components/FooterHTAG';

export const PrivacyPolicy = () => {
  return (
    <div className="bg-[#181C25] text-white min-h-screen lg:ml-16">
      <NavbarHTAG />

      {/* Bouton retour */}
      <div className="pt-32 px-4 lg:px-8 relative z-10">
        <div className="container mx-auto mb-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold hover:translate-x-1 duration-300"
          >
            <ArrowLeft size={18} />
            <span>Retour à l'accueil</span>
          </Link>
        </div>
      </div>

      {/* Contenu */}
      <section className="py-24 px-4 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-montserrat font-bold mb-4">
              Politique de <span className="text-blue-500">Confidentialité</span>
            </h1>
            <p className="text-gray-400 mb-12">
              Dernière mise à jour : 8 février 2026
            </p>

            <div className="space-y-12">
              {/* Introduction */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  Introduction
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    HTAG Facility respecte votre vie privée et s'engage à protéger vos données personnelles. 
                    Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
                  </p>
                </div>
              </div>

              {/* Données collectées */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  1. Données Personnelles Collectées
                </h2>
                <div className="text-gray-300 space-y-3">
                  <p className="font-semibold text-white">Via le formulaire de contact :</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Nom</li>
                    <li>Adresse email</li>
                    <li>Société/organisation</li>
                    <li>Budget estimé</li>
                    <li>Description du projet (message)</li>
                  </ul>
                  <p className="font-semibold text-white mt-4">Via les cookies et analytics :</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Adresse IP</li>
                    <li>Type de navigateur et appareil</li>
                    <li>Pages visitées</li>
                    <li>Temps passé sur le site</li>
                    <li>Source de trafic</li>
                  </ul>
                </div>
              </div>

              {/* Utilisation des données */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  2. Utilisation des Données
                </h2>
                <div className="text-gray-300 space-y-2">
                  <ul className="list-disc list-inside space-y-2 ml-2">
                    <li>Répondre à vos demandes de contact</li>
                    <li>Améliorer notre site et nos services</li>
                    <li>Analyser les statistiques d'utilisation</li>
                    <li>Vous envoyer des communications (avec consentement)</li>
                    <li>Prévenir les fraudes et abus</li>
                  </ul>
                </div>
              </div>

              {/* Partage des données */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  3. Partage des Données
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations avec :
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-2">
                    <li>Nos prestataires techniques (hébergement, email)</li>
                    <li>Les autorités légales (si légalement obligatoire)</li>
                    <li>Nos partenaires avec votre consentement explicite</li>
                  </ul>
                </div>
              </div>

              {/* Cookies */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  4. Cookies
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    Notre site utilise des cookies pour améliorer votre expérience. Vous pouvez contrôler les cookies 
                    via les paramètres de votre navigateur ou notre banneau de consentement.
                  </p>
                  <p className="font-semibold text-white mt-4">Types de cookies :</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Essentiels :</strong> Fonction du site</li>
                    <li><strong>Analytics :</strong> Compréhension du trafic</li>
                    <li><strong>Préférences :</strong> Vos paramètres personnels</li>
                  </ul>
                </div>
              </div>

              {/* Sécurité */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  5. Sécurité des Données
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données. 
                    Cependant, aucun système de transmission Internet n'est 100% sécurisé.
                  </p>
                </div>
              </div>

              {/* Droits */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  6. Vos Droits
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>Selon le RGPD, vous avez le droit de :</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Accéder à vos données</li>
                    <li>Corriger vos informations</li>
                    <li>Demander la suppression de vos données</li>
                    <li>Retirer votre consentement</li>
                    <li>Demander la portabilité de vos données</li>
                  </ul>
                  <p className="mt-4">
                    Pour exercer ces droits, contactez-nous à : <span className="text-blue-400">contact@htagfacility.fr</span>
                  </p>
                </div>
              </div>

              {/* Conservation */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  7. Conservation des Données
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    Les données du formulaire sont conservées le temps nécessaire pour traiter votre demande 
                    et respecter nos obligations légales (3 ans).
                  </p>
                  <p>
                    Les données analytics sont conservées pendant 12 mois.
                  </p>
                </div>
              </div>

              {/* Modifications */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  8. Modifications de la Politique
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    Nous pouvons mettre à jour cette politique de confidentialité. Les modifications 
                    significatives seront notifiées via email ou une bannière sur le site.
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h2 className="text-2xl font-montserrat font-bold text-white mb-4">
                  9. Contact
                </h2>
                <div className="text-gray-300 space-y-2">
                  <p>
                    Pour toute question concernant cette politique, contactez-nous à :
                  </p>
                  <p className="text-blue-400 font-semibold">contact@htagfacility.fr</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <FooterHTAG />
    </div>
  );
};

export default PrivacyPolicy;
